//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { AggregatedStatsDetails } from '@apis/rights-insights/rightsInsightsApiModel';
import { Alert } from '@components/atoms/alert/Alert';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import StatBubble from '@components/molecules/stat-bubble/StatBubble';
import { getComparisonStat } from '@components/molecules/stat-bubble/statComparisonUtils';
import GapGrid from '@components/organisms/gap-grid/GapGrid';
import {
  StatTitles,
  statMappings,
} from '@insights/constants/rightsInsightsConstants';
import { readableNumber } from '@utils/readableNumber';

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const segregateStats = (
  stats: AggregatedStatsDetails,
): Record<keyof typeof statMappings, { value: number; change: number }> => {
  return (Object.entries(statMappings) as Entries<typeof statMappings>).reduce<
    Record<keyof typeof statMappings, { value: number; change: number }>
  >(
    (acc, [title, mapping]) => {
      const { changeKey, valueKey } = mapping;
      acc[title] = {
        value: stats[valueKey] || 0,
        change: stats[changeKey] || 0,
      };
      return acc;
    },
    {
      totalRequested: { value: 0, change: 0 },
      totalApproved: { value: 0, change: 0 },
      totalRejected: { value: 0, change: 0 },
      totalPending: { value: 0, change: 0 },
    },
  );
};

interface RightsInsightsStatBubblesProps {
  dateRange: IAliasedDateRange;
  statsDetails?: AggregatedStatsDetails;
  isError: boolean;
}

export const RightsInsightsStatBubbles = ({
  dateRange,
  isError,
  statsDetails,
}: RightsInsightsStatBubblesProps) => {
  const mappedStats = statsDetails && segregateStats(statsDetails);

  //TODO:PD-263978 - Update the error handling state for the table
  if (isError) {
    return (
      <Alert type='error'>
        Failed to load Aggregated Rights Insights Metrics
      </Alert>
    );
  }

  return (
    <StyledGapGrid>
      {mappedStats &&
        (Object.entries(mappedStats) as Entries<typeof mappedStats>).map(
          ([title, stat]) => {
            const mapping = statMappings[title];

            if (!mapping) {
              return null;
            }
            const { statTooltip: statToolTip, title: displayTitle } = mapping;

            const displayValue = readableNumber(stat.value);
            const isPendingStat = title.includes('totalPending');

            return (
              <StatBubble
                key={title}
                comparisonStat={
                  !isPendingStat
                    ? getComparisonStat({
                        stat: stat.value,
                        comparisonStat: stat.change,
                        title: StatTitles[title],
                        readableNumber: readableNumber(stat.value),
                        dateRange: dateRange,
                        isPlural: true,
                      })
                    : undefined
                }
                helpText={statToolTip}
                title={displayTitle}
                value={displayValue}
              />
            );
          },
        )}
    </StyledGapGrid>
  );
};

const StyledGapGrid = styled(GapGrid)`
  grid-template-columns: repeat(4, 1fr);
`;
